/**
 * 사흘 — **문서 증거 28점.**
 *
 * `07_단서_98오브젝트_초안.md` 의 AR03~AR07 · FR07~FR09 · PH03 · PT02 · PT03 · CA03 ·
 * WS03 · PR01~PR08 (19점) 과, 98행을 조각과 하나씩 대조해서 찾아낸 **누락 9점**
 * (CC03 · CC09/CC10 · PT01 힌트 · CA06 힌트 · WS04 · WS08 · EX02 · EX03 · EX04) 이다.
 * 뒤의 아홉은 초안에 행이 있는데 조각이 아예 없던 것들이라, 그중 둘(소성표 · 로스팅
 * 프로파일)은 **없으면 기믹의 코드를 찾을 데가 없어진다.** 전부 「인쇄물」이라 3D 형태로는 서로 구별되지 않는다 —
 * **구별되는 것은 인쇄면뿐이고, 인쇄면이 곧 단서다.** 그래서 몸통은 네 가지 틀
 * (철한 보고서 · 서류철 · 낱장 · 클립보드)로 돌려 쓰고 힘은 전부 draw 에 넣는다.
 *
 * **한글을 그리지 않는다.** 서버에 한글 폰트가 없어 `fillText` 로 한글을 찍으면 두부가
 * 나온다. 글줄은 `stroke`/`body` 의 지터 선으로, 숫자·기호만 `num` 으로 찍는다.
 * 그래서 「무슨 서류인가」는 글자가 아니라 **표·막대·도장·체크·붉은 표시의 배치**로
 * 읽혀야 한다. 이게 이 파일의 설계 전부다.
 */

import { bevelBox } from './kernel';
import type { Piece } from './pieces';
import { printedFace, rnd, stroke } from './saholEvidence';
import { num, body, table, seal, facingUp, PAPER_BG } from './saholPapers';

/* ------------------------------------------------------------------ *
 * 그리기 도구 — 「손으로 쓴 것」과 「나중에 표시한 것」
 * ------------------------------------------------------------------ */

/** 손글씨 한 줄. 인쇄 글줄(stroke)보다 굴곡이 크고 기울기가 있어야 손으로 읽힌다 */
function hand(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  wid: number,
  h: number,
  seed: number,
  color = 'rgba(38,52,92,0.8)',
) {
  g.strokeStyle = color;
  g.lineWidth = Math.max(1.5, h * 0.22);
  g.lineCap = 'round';
  g.beginPath();
  g.moveTo(x, y);
  const n = 14;
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    g.lineTo(x + wid * t, y - Math.sin(t * 9 + seed) * h * 0.42 - (rnd(seed + i) - 0.5) * h * 0.3);
  }
  g.stroke();
}

/** 체크 표시 */
function check(g: CanvasRenderingContext2D, x: number, y: number, s: number, color = '#2f4b34') {
  g.strokeStyle = color;
  g.lineWidth = Math.max(2, s * 0.2);
  g.lineCap = 'round';
  g.beginPath();
  g.moveTo(x - s * 0.4, y);
  g.lineTo(x - s * 0.08, y + s * 0.36);
  g.lineTo(x + s * 0.45, y - s * 0.42);
  g.stroke();
}

/** 가위표 */
function cross(g: CanvasRenderingContext2D, x: number, y: number, s: number, color = '#b6382b') {
  g.strokeStyle = color;
  g.lineWidth = Math.max(2, s * 0.18);
  g.lineCap = 'round';
  g.beginPath();
  g.moveTo(x - s * 0.4, y - s * 0.4);
  g.lineTo(x + s * 0.4, y + s * 0.4);
  g.moveTo(x + s * 0.4, y - s * 0.4);
  g.lineTo(x - s * 0.4, y + s * 0.4);
  g.stroke();
}

/** 나중에 사람이 손으로 두른 붉은 동그라미. 두 번 겹쳐 그려야 「나중에 표시한 것」이 된다 */
function ring(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
  seed: number,
  color = 'rgba(178,52,40,0.82)',
) {
  g.strokeStyle = color;
  g.lineWidth = Math.max(2, ry * 0.16);
  for (let k = 0; k < 2; k++) {
    g.beginPath();
    for (let i = 0; i <= 26; i++) {
      const a = (i / 26) * Math.PI * 2 + k * 0.4;
      const j = 1 + (rnd(seed + i + k * 7) - 0.5) * 0.09;
      const px = x + Math.cos(a) * rx * j;
      const py = y + Math.sin(a) * ry * j;
      if (i === 0) g.moveTo(px, py);
      else g.lineTo(px, py);
    }
    g.stroke();
  }
}

/** 빈 네모칸 */
function boxMark(g: CanvasRenderingContext2D, x: number, y: number, s: number) {
  g.strokeStyle = 'rgba(40,52,70,0.6)';
  g.lineWidth = 1.8;
  g.strokeRect(x - s / 2, y - s / 2, s, s);
}

/** 문서 머리 — 제목 두 줄과 그 아래 가로줄. 어느 서류에나 있어야 서류로 보인다 */
function head(g: CanvasRenderingContext2D, W: number, H: number, seed: number, tone = '#2b3444') {
  // **제목은 낱말 셋으로 끊어 긋는다.** 한 줄로 이으면 글줄이 아니라 검은 먹칠 막대로
  // 보인다 — 문서 19점이 전부 똑같은 검은 바를 이고 있었다.
  g.strokeStyle = tone;
  let tx = W * 0.1;
  for (let i = 0; i < 3; i++) {
    const tw = W * (0.11 + rnd(seed + i * 2.3) * 0.1);
    stroke(g, tx, H * 0.085, tx + tw, H * 0.085, H * 0.026, seed + i);
    tx += tw + W * 0.03;
  }
  g.strokeStyle = 'rgba(40,52,70,0.5)';
  stroke(g, W * 0.1, H * 0.125, W * 0.44, H * 0.125, H * 0.012, seed + 3);
  g.lineWidth = 2;
  g.beginPath();
  g.moveTo(W * 0.08, H * 0.15);
  g.lineTo(W * 0.92, H * 0.15);
  g.stroke();
}

/** 세로 막대 그래프. 기준선을 넘는지 아닌지가 곧 단서인 검사성적서에 쓴다 */
function bars(
  g: CanvasRenderingContext2D,
  x: number,
  y: number,
  wid: number,
  hgt: number,
  vals: number[],
  limit: number,
) {
  g.strokeStyle = 'rgba(40,52,70,0.45)';
  g.lineWidth = 1.6;
  g.beginPath();
  g.moveTo(x, y + hgt);
  g.lineTo(x + wid, y + hgt);
  g.stroke();
  const bw = (wid / vals.length) * 0.56;
  vals.forEach((v, i) => {
    const bx = x + (wid / vals.length) * (i + 0.22);
    const bh = hgt * Math.min(1, v);
    g.fillStyle = v > limit ? 'rgba(168,60,46,0.72)' : 'rgba(58,74,96,0.6)';
    g.fillRect(bx, y + hgt - bh, bw, bh);
  });
  // 기준선 — 빨간 파선
  g.strokeStyle = '#b6382b';
  g.lineWidth = 2.4;
  g.setLineDash([9, 7]);
  g.beginPath();
  g.moveTo(x - wid * 0.03, y + hgt - hgt * limit);
  g.lineTo(x + wid * 1.03, y + hgt - hgt * limit);
  g.stroke();
  g.setLineDash([]);
}

/* ------------------------------------------------------------------ *
 * 몸통 네 가지
 * ------------------------------------------------------------------ */

type Form = 'report' | 'folder' | 'loose' | 'clipboard';

/** 각 틀의 치수. printed face 를 어디에 놓을지까지 여기서 정한다 */
const FORM_DIMS: Record<Form, { w: number; d: number; h: number }> = {
  report: { w: 0.21, d: 0.297, h: 0.007 },
  folder: { w: 0.238, d: 0.318, h: 0.022 },
  loose: { w: 0.21, d: 0.297, h: 0.0016 },
  clipboard: { w: 0.232, d: 0.33, h: 0.012 },
};

function buildForm(
  w: Parameters<Piece['build']>[0],
  d: { w: number; d: number; h: number },
  form: Form,
  seed: number,
) {
  if (form === 'report') {
    // 스테이플로 왼쪽 위를 찍은 A4 보고서. 낱장이 조금씩 어긋나야 「묶음」으로 읽힌다
    w.part('속지');
    const blk = bevelBox(d.w, d.h - 0.0016, d.d, 0.0012);
    blk.translate(0, (d.h - 0.0016) / 2, 0);
    w.add(blk, 'paper', 0.2);
    for (let i = 0; i < 3; i++) {
      const s = bevelBox(d.w - 0.002, 0.0009, d.d - 0.002, 0.0004);
      s.rotateY(0.006 + rnd(seed + i) * 0.018);
      s.translate((rnd(seed + i * 3) - 0.5) * 0.004, 0.0012 + i * 0.0011, (rnd(seed + i * 7) - 0.5) * 0.005);
      w.add(s, 'paper', 0.2);
    }
    w.part('겉장');
    const top = bevelBox(d.w, 0.0016, d.d, 0.0006);
    top.translate(0, d.h - 0.0008, 0);
    w.add(top, 'paper', 0.2);
    w.part('말린 귀');
    // 오른쪽 아래 귀가 들려 있어야 「손이 여러 번 간 서류」가 된다
    const curl = bevelBox(0.044, 0.0011, 0.044, 0.0004);
    curl.rotateZ(-0.3);
    curl.rotateX(0.26);
    curl.translate(d.w / 2 - 0.021, d.h + 0.005, d.d / 2 - 0.021);
    w.add(curl, 'paper', 0.2);
    w.part('스테이플');
    const st = bevelBox(0.012, 0.0016, 0.0022, 0.0005);
    st.rotateY(Math.PI / 4);
    st.translate(-d.w / 2 + 0.016, d.h + 0.0004, -d.d / 2 + 0.016);
    w.add(st, 'steel', 0.1);
    w.part('표지 인쇄');
    w.thinSurface(() => {
      const p = facingUp(d.w * 0.96, d.d * 0.96);
      p.translate(0, d.h + 0.0006, 0);
      w.add(p, 'painted', 0);
    });
    return d.h + 0.0006;
  }

  if (form === 'folder') {
    // 마닐라 서류철. 표지가 살짝 들려 있고 속지가 삐져나온다
    const cov = 0.0022;
    w.part('아래 표지');
    const bot = bevelBox(d.w, cov, d.d, 0.002);
    bot.translate(0, cov / 2, 0);
    w.add(bot, 'cardboard', 0.3);
    w.part('속지');
    const blk = bevelBox(d.w - 0.012, d.h - cov * 2, d.d - 0.01, 0.0012);
    blk.translate(0, cov + (d.h - cov * 2) / 2, 0.002);
    w.add(blk, 'paper', 0.2);
    w.part('삐져나온 낱장');
    for (let i = 0; i < 2; i++) {
      const s = bevelBox(d.w - 0.02, 0.0011, d.d + 0.014, 0.0004);
      s.rotateY(0.028 - i * 0.062);
      s.translate(0.004 - i * 0.006, cov + (d.h - cov * 2) * (0.36 + i * 0.38), 0.01 + i * 0.004);
      w.add(s, 'paper', 0.2);
    }
    w.part('앞표지');
    const topc = bevelBox(d.w, cov, d.d, 0.002);
    topc.rotateX(-0.03);
    topc.translate(0, d.h - cov / 2, 0);
    w.add(topc, 'cardboard', 0.3);
    w.part('색인 탭');
    for (let i = 0; i < 2; i++) {
      const tab = bevelBox(0.026, 0.0024, 0.05, 0.0008);
      tab.translate(d.w / 2 + 0.01, cov + 0.005 + i * 0.008, -d.d * 0.2 + i * d.d * 0.3);
      w.add(tab, 'paper', 0.2);
    }
    w.part('표지 인쇄');
    w.thinSurface(() => {
      const p = facingUp(d.w * 0.84, d.d * 0.86);
      p.rotateX(-0.03);
      p.translate(0, d.h + 0.0008, 0.001);
      w.add(p, 'painted', 0);
    });
    return d.h + 0.0008;
  }

  if (form === 'clipboard') {
    w.part('판');
    const bd = bevelBox(d.w, 0.005, d.d, 0.0015);
    bd.translate(0, 0.0025, 0);
    w.add(bd, 'cardboard', 0.3);
    w.part('종이');
    const sh = bevelBox(d.w - 0.014, 0.0022, d.d - 0.022, 0.0008);
    sh.translate(0, 0.0061, -0.006);
    w.add(sh, 'paper', 0.2);
    w.part('집게');
    // **집게는 +Z 쪽, 즉 문서의 머리맡에 있다.** `facingUp` 은 PlaneGeometry 를
    // rotateX(-PI/2) 한 뒤 rotateY(PI) 하므로 캔버스 위쪽이 **+Z** 로 간다. 처음에
    // -d.d/2 에 달았더니 클립이 문서 발치를 물어 lamp_inspect 의 서명란을 덮었다.
    //
    // **집게는 alu 다.** steel(metalness .82) 은 카탈로그 씬에 환경맵이 없어서
    // 정반사가 걸리는 각도 말고는 새까맣게 나온다 — correction_list·keyuse_register 에서
    // 집게가 검은 덩어리로 찍혔다. alu(.58) 는 같은 씬에서 은색으로 읽힌다.
    const cl = bevelBox(0.072, 0.009, 0.026, 0.002);
    cl.translate(0, 0.0095, d.d / 2 - 0.017);
    const lip = bevelBox(0.062, 0.0022, 0.014, 0.0006);
    lip.rotateX(-0.22);
    lip.translate(0, 0.0138, d.d / 2 - 0.03);
    // 집게 두 조각을 movable 로 — openable 을 단 조각(소성표)에서 머리맡 축으로 젖혀 연다
    w.movable('집게', () => {
      w.add(cl, 'alu', 0.2);
      w.add(lip, 'alu', 0.1);
    });
    w.part('표지 인쇄');
    w.thinSurface(() => {
      const p = facingUp(d.w - 0.018, d.d - 0.026);
      p.translate(0, 0.0074, -0.006);
      w.add(p, 'painted', 0);
    });
    return 0.0074;
  }

  // loose — 낱장 한 장. 끝이 살짝 말려 올라가 그림자가 생겨야 종이로 보인다.
  //
  // **인쇄면은 한 장만 깐다.** 처음에는 말린 끝에도 printedFace 를 한 장 더 깔았는데,
  // 같은 재질이라 같은 텍스처가 통째로 다시 그려져 **문서가 두 번 인쇄된 것처럼** 보였다
  // (repay_clause·sponsor_clause·apology_script 셋 다 위쪽에 눌린 사본이 하나씩 더 있었다).
  // 말린 끝은 종이의 뒷면이니 백지가 맞다.
  w.part('낱장');
  const main = bevelBox(d.w, d.h, d.d * 0.86, 0.0005);
  main.translate(0, d.h / 2 + 0.0004, -d.d * 0.07);
  w.add(main, 'paper', 0.25);
  w.part('말린 끝');
  const lift = bevelBox(d.w, d.h, d.d * 0.16, 0.0005);
  lift.rotateX(-0.24);
  lift.translate(0, d.h / 2 + 0.0026, d.d * 0.435);
  w.add(lift, 'paper', 0.25);
  w.part('인쇄면');
  w.thinSurface(() => {
    const p = facingUp(d.w * 0.97, d.d * 0.84);
    p.translate(0, d.h + 0.0007, -d.d * 0.07);
    w.add(p, 'painted', 0);
  });
  return d.h + 0.0007;
}

/* ------------------------------------------------------------------ *
 * 조각 공장
 * ------------------------------------------------------------------ */

type Spec = {
  id: string;
  name: string;
  note: string;
  form: Form;
  seed: number;
  bg?: string;
  mount?: Piece['mount'];
  draw: (g: CanvasRenderingContext2D, W: number, H: number) => void;
};

function sheetPiece(sp: Spec): Piece {
  const d = FORM_DIMS[sp.form];
  return {
    id: sp.id,
    name: sp.name,
    collection: 'sahol',
    category: '단서',
    note: sp.note,
    dims: { w: d.w, d: d.d, h: d.h },
    clusters: 1,
    mount: sp.mount,
    build(w) {
      buildForm(w, d, sp.form, sp.seed);
    },
    materials: () => ({ painted: printedFace(sp.draw, 512, 700, sp.bg ?? PAPER_BG) }),
  };
}

export const SAHOL_SHEET_PIECES: Piece[] = [
  /* ---- 기록실 AR ---- */
  sheetPiece({
    id: 'correction_list',
    name: '정정자료 수집목록',
    form: 'clipboard',
    seed: 11,
    note:
      'AR03. 한서진이 모은 정정자료 목록. **다섯 칸이 전부 체크돼 있다는 것이 요점이다** — ' +
      '한 사람만 노린 것이 아니라 다섯 명 전부를 훑고 있었다는 뜻이라, 체크가 넷이거나 ' +
      '하나가 비면 단서가 뒤집힌다. 다섯 줄 · 다섯 체크를 반드시 지킨다.',
    draw: (g, W, H) => {
      head(g, W, H, 5);
      for (let i = 0; i < 5; i++) {
        const y = H * (0.26 + i * 0.115);
        boxMark(g, W * 0.15, y, H * 0.03);
        check(g, W * 0.15, y, H * 0.032);
        g.strokeStyle = 'rgba(38,48,64,0.72)';
        stroke(g, W * 0.24, y + H * 0.012, W * (0.44 + rnd(i * 5) * 0.16), y + H * 0.012, H * 0.022, i * 7);
        num(g, `0${i + 1}`, W * 0.86, y + H * 0.012, H * 0.028, 'rgba(40,52,70,0.6)', 'right');
      }
      g.strokeStyle = 'rgba(40,52,70,0.4)';
      g.lineWidth = 1.6;
      g.beginPath();
      g.moveTo(W * 0.1, H * 0.86);
      g.lineTo(W * 0.9, H * 0.86);
      g.stroke();
      num(g, '5 / 5', W * 0.9, H * 0.92, H * 0.04, '#2f4b34', 'right');
    },
  }),

  sheetPiece({
    id: 'photo_report',
    mount: 'surface',
    name: '사진 성과보고서',
    form: 'report',
    seed: 23,
    note:
      'AR04. 촬영일과 행사일을 나란히 적은 성과보고서. **두 날짜가 어긋난 줄이 눈에 띄어야 한다** — ' +
      '표만 있고 표시가 없으면 그냥 서류다. 어긋난 두 줄에 나중에 손으로 두른 붉은 동그라미가 ' +
      '있어야 「누군가 이미 알아챘다」가 읽힌다.',
    draw: (g, W, H) => {
      head(g, W, H, 9);
      num(g, '2024', W * 0.9, H * 0.115, H * 0.03, 'rgba(40,52,70,0.6)', 'right');
      const x = W * 0.1;
      const y = H * 0.22;
      const wid = W * 0.8;
      const hgt = H * 0.56;
      g.strokeStyle = 'rgba(40,52,70,0.5)';
      g.lineWidth = 1.6;
      g.strokeRect(x, y, wid, hgt);
      for (let r = 1; r < 7; r++) {
        g.beginPath();
        g.moveTo(x, y + (hgt * r) / 7);
        g.lineTo(x + wid, y + (hgt * r) / 7);
        g.stroke();
      }
      for (const c of [0.42, 0.72]) {
        g.beginPath();
        g.moveTo(x + wid * c, y);
        g.lineTo(x + wid * c, y + hgt);
        g.stroke();
      }
      g.fillStyle = 'rgba(52,66,86,0.14)';
      g.fillRect(x, y, wid, hgt / 7);
      const shot = ['03.11', '05.02', '06.18', '08.24', '09.07', '11.02'];
      const evt = ['03.11', '05.02', '06.09', '08.24', '09.30', '11.02'];
      for (let i = 0; i < 6; i++) {
        const ry = y + (hgt * (i + 1.68)) / 7;
        g.strokeStyle = 'rgba(38,48,64,0.66)';
        stroke(g, x + wid * 0.04, ry, x + wid * 0.36, ry, H * 0.02, i * 3);
        num(g, shot[i], x + wid * 0.46, ry + H * 0.008, H * 0.028, '#2b3444');
        num(g, evt[i], x + wid * 0.76, ry + H * 0.008, H * 0.028, '#2b3444');
        if (shot[i] !== evt[i]) ring(g, x + wid * 0.86, ry - H * 0.004, wid * 0.14, H * 0.03, i * 11);
      }
      g.strokeStyle = 'rgba(178,52,40,0.8)';
      stroke(g, W * 0.12, H * 0.85, W * 0.5, H * 0.85, H * 0.022, 41);
    },
  }),

  sheetPiece({
    id: 'inspect_index',
    name: '제품 검사 파일 색인',
    form: 'folder',
    seed: 31,
    note:
      'AR05. 검사파일 색인. **1차 검사 줄만 쪽수 칸이 비어 있다** — 항목은 있는데 본문이 없다는 ' +
      '뜻이고, 그 빈칸 하나가 이 물건의 전부다. 다른 줄에는 전부 쪽수가 있어야 빈칸이 보인다. ' +
      '빈칸 옆의 붉은 물음표는 누군가 이미 이상하게 여겼다는 표시다.',
    bg: '#dccbaa',
    draw: (g, W, H) => {
      head(g, W, H, 17, '#4a3c22');
      const pages = ['—', '014', '031', '046', '062', '077'];
      for (let i = 0; i < 6; i++) {
        const y = H * (0.24 + i * 0.1);
        num(g, `${i + 1}`, W * 0.12, y, H * 0.032, 'rgba(70,58,36,0.75)');
        g.strokeStyle = 'rgba(64,54,34,0.7)';
        stroke(g, W * 0.2, y - H * 0.008, W * (0.5 + rnd(i * 7) * 0.16), y - H * 0.008, H * 0.024, i * 5);
        g.strokeStyle = 'rgba(64,54,34,0.32)';
        g.lineWidth = 1.4;
        g.setLineDash([3, 5]);
        g.beginPath();
        g.moveTo(W * 0.2, y + H * 0.014);
        g.lineTo(W * 0.78, y + H * 0.014);
        g.stroke();
        g.setLineDash([]);
        num(g, pages[i], W * 0.86, y, H * 0.032, i === 0 ? '#a8392c' : '#463a22', 'right');
      }
      // 빈 본문 자리에 물음표
      num(g, '?', W * 0.93, H * 0.245, H * 0.05, '#b6382b', 'right');
      ring(g, W * 0.84, H * 0.235, W * 0.13, H * 0.035, 3);
      g.strokeStyle = 'rgba(64,54,34,0.55)';
      stroke(g, W * 0.12, H * 0.88, W * 0.46, H * 0.88, H * 0.022, 61);
    },
  }),

  sheetPiece({
    id: 'phase2_approval',
    name: '2단계 사업 승인서',
    form: 'report',
    seed: 43,
    note:
      'AR06. 2단계 사업 승인서. **최종승인이 아니라 「조건부 검토 중」이다** — 결재란 셋 중 ' +
      '첫 칸에만 도장이 찍혀 있고 마지막 칸에는 도장 대신 붉은 가위표가 있어야 한다. ' +
      '차유리가 확정된 것처럼 판 근거라, 도장 셋이 다 찍히면 이 물건의 의미가 사라진다.',
    draw: (g, W, H) => {
      head(g, W, H, 13);
      body(g, W * 0.1, H * 0.21, W * 0.78, 4, H * 0.04, 71);
      // 상태 칸 — 두 네모 중 하나만 채워져 있다
      g.strokeStyle = 'rgba(40,52,70,0.6)';
      g.lineWidth = 2;
      g.strokeRect(W * 0.1, H * 0.4, W * 0.36, H * 0.09);
      g.strokeRect(W * 0.52, H * 0.4, W * 0.36, H * 0.09);
      g.fillStyle = 'rgba(190,150,52,0.28)';
      g.fillRect(W * 0.1, H * 0.4, W * 0.36, H * 0.09);
      check(g, W * 0.15, H * 0.445, H * 0.034, '#8a6a20');
      g.strokeStyle = 'rgba(60,50,30,0.75)';
      stroke(g, W * 0.2, H * 0.452, W * 0.42, H * 0.452, H * 0.022, 4);
      g.strokeStyle = 'rgba(40,52,70,0.35)';
      stroke(g, W * 0.58, H * 0.452, W * 0.84, H * 0.452, H * 0.022, 8);
      body(g, W * 0.1, H * 0.56, W * 0.78, 3, H * 0.04, 77);
      // 결재란 셋 중 가운데만 찍혔다
      seal(g, W * 0.4, H * 0.72, W * 0.16, 5);
      cross(g, W * 0.72 + W * 0.08, H * 0.79, H * 0.034);
      num(g, '2024.12', W * 0.1, H * 0.94, H * 0.03, 'rgba(40,52,70,0.6)');
    },
  }),

  sheetPiece({
    id: 'keyuse_register',
    name: '비상키 사용대장',
    form: 'clipboard',
    seed: 53,
    note:
      'AR07. 공용 비상키 사용대장. **자율기록 방식이라 18일 줄이 비어 있다** — 전자 로그가 아니라 ' +
      '손으로 적는 장부라서 안 적으면 그만이라는 것이 단서다. 15~17일은 손글씨로 차 있고 ' +
      '마지막 두 줄만 비어야 하며, 그 빈 줄에 날짜만 인쇄돼 있어야 「안 적힌 것」이 읽힌다.',
    draw: (g, W, H) => {
      head(g, W, H, 19);
      const x = W * 0.08;
      const y = H * 0.2;
      const wid = W * 0.84;
      const hgt = H * 0.64;
      g.strokeStyle = 'rgba(40,52,70,0.5)';
      g.lineWidth = 1.5;
      g.strokeRect(x, y, wid, hgt);
      for (let r = 1; r < 8; r++) {
        g.beginPath();
        g.moveTo(x, y + (hgt * r) / 8);
        g.lineTo(x + wid, y + (hgt * r) / 8);
        g.stroke();
      }
      for (const c of [0.2, 0.52, 0.76]) {
        g.beginPath();
        g.moveTo(x + wid * c, y);
        g.lineTo(x + wid * c, y + hgt);
        g.stroke();
      }
      g.fillStyle = 'rgba(52,66,86,0.14)';
      g.fillRect(x, y, wid, hgt / 8);
      const days = ['12.13', '12.14', '12.15', '12.16', '12.17', '12.18', '12.19'];
      for (let i = 0; i < 7; i++) {
        const ry = y + (hgt * (i + 1.66)) / 8;
        num(g, days[i], x + wid * 0.03, ry, H * 0.026, 'rgba(40,52,70,0.68)');
        if (i >= 5) continue; // 18·19일은 아무도 안 적었다
        hand(g, x + wid * 0.24, ry, wid * 0.22, H * 0.022, i * 3);
        hand(g, x + wid * 0.55, ry, wid * 0.17, H * 0.022, i * 5 + 1);
        hand(g, x + wid * 0.79, ry, wid * 0.14, H * 0.022, i * 7 + 2);
      }
      ring(g, x + wid * 0.5, y + (hgt * 6.6) / 8, wid * 0.48, H * 0.045, 9);
      num(g, '12.18', W * 0.9, H * 0.93, H * 0.036, '#b6382b', 'right');
    },
  }),

  /* ---- 재무실 FR ---- */
  sheetPiece({
    id: 'bridge_loan',
    name: '브리지 대출 잔액표',
    form: 'report',
    seed: 61,
    note:
      'FR07. 배정호의 브리지 대출 잔액표. **금액이 커야 동기가 된다** — 합계 칸의 ' +
      '390,000,000 이 이 서류의 전부다. 다른 숫자보다 두 배 크게 찍고 그 줄만 붉게 강조한다.',
    draw: (g, W, H) => {
      head(g, W, H, 29);
      table(g, W * 0.1, H * 0.2, W * 0.8, H * 0.42, 5, 3, 91);
      const rows = ['120,000,000', '95,000,000', '85,000,000', '90,000,000'];
      rows.forEach((t, i) => {
        num(g, t, W * 0.86, H * (0.2 + (0.42 * (i + 1.7)) / 5), H * 0.026, '#2b3444', 'right');
      });
      g.fillStyle = 'rgba(178,52,40,0.1)';
      g.fillRect(W * 0.1, H * 0.66, W * 0.8, H * 0.1);
      g.strokeStyle = 'rgba(178,52,40,0.55)';
      g.lineWidth = 2;
      g.strokeRect(W * 0.1, H * 0.66, W * 0.8, H * 0.1);
      g.strokeStyle = 'rgba(38,48,64,0.7)';
      stroke(g, W * 0.14, H * 0.725, W * 0.32, H * 0.725, H * 0.024, 5);
      num(g, '390,000,000', W * 0.86, H * 0.735, H * 0.052, '#a8392c', 'right');
      body(g, W * 0.1, H * 0.83, W * 0.7, 2, H * 0.04, 95);
    },
  }),

  sheetPiece({
    id: 'repay_clause',
    name: '자동상환 조건서',
    form: 'loose',
    seed: 71,
    note:
      'FR08. **[핵심]** 전환금이 들어오는 즉시 대출이 자동상환된다는 조항. **왜 하필 19일 18시까지 ' +
      '숨겨야 했는지가 여기서 나온다** — 그래서 `12/19 18:00` 이 문단 속에 묻히면 안 되고, ' +
      '노란 형광 띠와 붉은 밑줄로 한눈에 잡혀야 한다. 낱장이라 끝이 들려 있다.',
    draw: (g, W, H) => {
      head(g, W, H, 37);
      body(g, W * 0.1, H * 0.21, W * 0.78, 3, H * 0.042, 101);
      // 문제의 조항
      g.fillStyle = 'rgba(214,186,64,0.34)';
      g.fillRect(W * 0.08, H * 0.37, W * 0.84, H * 0.16);
      num(g, '3', W * 0.11, H * 0.42, H * 0.032, '#2b3444');
      g.strokeStyle = 'rgba(30,38,52,0.85)';
      stroke(g, W * 0.17, H * 0.415, W * 0.86, H * 0.415, H * 0.026, 7);
      stroke(g, W * 0.17, H * 0.455, W * 0.62, H * 0.455, H * 0.026, 11);
      num(g, '12 / 19  18:00', W * 0.17, H * 0.512, H * 0.044, '#a8392c');
      g.strokeStyle = '#b6382b';
      g.lineWidth = 3;
      g.beginPath();
      g.moveTo(W * 0.16, H * 0.525);
      g.lineTo(W * 0.66, H * 0.525);
      g.stroke();
      body(g, W * 0.1, H * 0.58, W * 0.78, 4, H * 0.042, 107);
      seal(g, W * 0.56, H * 0.78, W * 0.13, 3);
    },
  }),

  sheetPiece({
    id: 'loan_amend',
    name: '대출 변경계약 승인란',
    form: 'folder',
    seed: 83,
    note:
      'FR09. 대출 변경계약서의 승인란. **이사회 승인 칸이 비어 있고 대표 전자승인 칸만 차 있다** — ' +
      '네 칸 중 하나만 서명되고 셋이 비어야 하며, 빈 칸 위의 붉은 가위표가 「없는 것이 문제」라고 ' +
      '말해 준다. 넷 다 차면 그냥 정상 서류가 된다.',
    bg: '#dccbaa',
    draw: (g, W, H) => {
      head(g, W, H, 41, '#4a3c22');
      body(g, W * 0.1, H * 0.2, W * 0.78, 3, H * 0.04, 113, 'rgba(64,54,34,0.6)');
      const bx = W * 0.1;
      const by = H * 0.38;
      const bw = W * 0.8;
      const bh = H * 0.24;
      g.strokeStyle = 'rgba(64,54,34,0.6)';
      g.lineWidth = 2;
      for (let i = 0; i < 4; i++) {
        g.strokeRect(bx + (bw / 4) * i, by, bw / 4, bh);
        g.strokeStyle = 'rgba(64,54,34,0.4)';
        stroke(g, bx + (bw / 4) * i + bw * 0.03, by + H * 0.03, bx + (bw / 4) * (i + 1) - bw * 0.03, by + H * 0.03, H * 0.016, i);
        g.strokeStyle = 'rgba(64,54,34,0.6)';
      }
      // 첫 칸만 전자승인 서명이 있다
      hand(g, bx + bw * 0.04, by + bh * 0.68, bw * 0.17, H * 0.03, 2, 'rgba(38,52,92,0.8)');
      for (let i = 1; i < 4; i++) cross(g, bx + (bw / 4) * (i + 0.5), by + bh * 0.62, H * 0.038);
      g.strokeStyle = 'rgba(178,52,40,0.8)';
      stroke(g, W * 0.1, H * 0.7, W * 0.56, H * 0.7, H * 0.024, 17);
      body(g, W * 0.1, H * 0.78, W * 0.74, 3, H * 0.04, 119, 'rgba(64,54,34,0.55)');
    },
  }),

  /* ---- 사진실 PH ---- */
  sheetPiece({
    id: 'raw_compare',
    name: 'RAW · JPEG 날짜 비교표',
    form: 'report',
    seed: 97,
    note:
      'PH03. 원본 RAW 의 촬영일과 제출한 JPEG 의 날짜를 나란히 놓은 표. **두 열이 달라야 단서다** — ' +
      '여섯 줄 중 셋이 어긋나고, 어긋난 줄 사이를 잇는 붉은 선이 있어야 「비교표」로 읽힌다. ' +
      '표만 있으면 사무용지다.',
    bg: '#e8eaea',
    draw: (g, W, H) => {
      head(g, W, H, 103);
      const raw = ['03.11', '04.28', '06.09', '08.24', '09.07', '11.02'];
      const jpg = ['03.11', '05.02', '06.18', '08.24', '09.30', '11.02'];
      g.strokeStyle = 'rgba(40,52,70,0.4)';
      g.lineWidth = 1.5;
      g.strokeRect(W * 0.1, H * 0.2, W * 0.3, H * 0.6);
      g.strokeRect(W * 0.6, H * 0.2, W * 0.3, H * 0.6);
      g.fillStyle = 'rgba(52,66,86,0.14)';
      g.fillRect(W * 0.1, H * 0.2, W * 0.3, H * 0.075);
      g.fillRect(W * 0.6, H * 0.2, W * 0.3, H * 0.075);
      num(g, 'RAW', W * 0.13, H * 0.262, H * 0.032, '#2b3444');
      num(g, 'JPEG', W * 0.63, H * 0.262, H * 0.032, '#2b3444');
      for (let i = 0; i < 6; i++) {
        const y = H * (0.35 + i * 0.075);
        num(g, raw[i], W * 0.14, y, H * 0.03, '#2b3444');
        num(g, jpg[i], W * 0.64, y, H * 0.03, '#2b3444');
        g.strokeStyle = raw[i] === jpg[i] ? 'rgba(40,52,70,0.22)' : 'rgba(178,52,40,0.85)';
        g.lineWidth = raw[i] === jpg[i] ? 1.4 : 2.6;
        g.beginPath();
        g.moveTo(W * 0.41, y - H * 0.01);
        g.lineTo(W * 0.59, y - H * 0.01);
        g.stroke();
        if (raw[i] !== jpg[i]) cross(g, W * 0.5, y - H * 0.01, H * 0.03);
      }
      num(g, '3 / 6', W * 0.9, H * 0.87, H * 0.04, '#a8392c', 'right');
    },
  }),

  /* ---- 생산·검사 PT ---- */
  sheetPiece({
    id: 'safety_test1',
    name: '1차 안전검사 성적서',
    form: 'report',
    seed: 109,
    note:
      'PT02. 1차 안전검사 성적서. **특정 생산분이 기준을 넘었다** — 붉은 파선(기준)을 넘는 막대가 ' +
      '둘 있어야 하고 그 막대만 붉어야 한다. PT03(2차)과 **같은 양식**이라야 나란히 놓았을 때 ' +
      '「같은 검사인데 결과가 뒤집혔다」가 읽히므로 두 조각의 레이아웃을 일부러 똑같이 둔다.',
    draw: (g, W, H) => {
      head(g, W, H, 127);
      num(g, '1', W * 0.9, H * 0.115, H * 0.04, '#2b3444', 'right');
      bars(g, W * 0.13, H * 0.24, W * 0.74, H * 0.36, [0.42, 0.51, 0.88, 0.46, 0.79, 0.38], 0.66);
      ring(g, W * 0.13 + W * 0.74 * 0.42, H * 0.34, W * 0.09, H * 0.12, 21);
      table(g, W * 0.13, H * 0.66, W * 0.74, H * 0.16, 3, 3, 131);
      g.fillStyle = 'rgba(178,52,40,0.12)';
      g.fillRect(W * 0.13, H * 0.86, W * 0.74, H * 0.08);
      g.strokeStyle = '#b6382b';
      g.lineWidth = 2;
      g.strokeRect(W * 0.13, H * 0.86, W * 0.74, H * 0.08);
      cross(g, W * 0.2, H * 0.9, H * 0.036);
      g.strokeStyle = 'rgba(168,57,44,0.85)';
      stroke(g, W * 0.26, H * 0.905, W * 0.66, H * 0.905, H * 0.026, 23);
    },
  }),

  sheetPiece({
    id: 'safety_test2',
    name: '2차 안전검사 성적서',
    form: 'report',
    seed: 113,
    note:
      'PT03. 조건을 조정해 다시 한 2차 검사. **PT02 와 같은 양식에 막대만 기준 아래로 내려간다** — ' +
      '「이미 해결된 문제」라는 윤가람의 자기합리화가 이 한 장이다. 아래 판정칸은 붉은 가위표가 ' +
      '아니라 푸른 체크여야 하고, 그 색 차이가 두 조각을 가른다.',
    draw: (g, W, H) => {
      head(g, W, H, 127);
      num(g, '2', W * 0.9, H * 0.115, H * 0.04, '#2b3444', 'right');
      bars(g, W * 0.13, H * 0.24, W * 0.74, H * 0.36, [0.36, 0.44, 0.58, 0.4, 0.52, 0.33], 0.66);
      table(g, W * 0.13, H * 0.66, W * 0.74, H * 0.16, 3, 3, 131);
      g.fillStyle = 'rgba(46,92,132,0.1)';
      g.fillRect(W * 0.13, H * 0.86, W * 0.74, H * 0.08);
      g.strokeStyle = 'rgba(46,92,132,0.7)';
      g.lineWidth = 2;
      g.strokeRect(W * 0.13, H * 0.86, W * 0.74, H * 0.08);
      check(g, W * 0.2, H * 0.898, H * 0.038, '#2e5c84');
      g.strokeStyle = 'rgba(46,92,132,0.8)';
      stroke(g, W * 0.26, H * 0.905, W * 0.6, H * 0.905, H * 0.026, 29);
      // 조건을 바꿨다는 각주가 작게 붙어 있다 — 이게 없으면 그냥 합격증이다
      g.strokeStyle = 'rgba(40,52,70,0.45)';
      stroke(g, W * 0.13, H * 0.955, W * 0.72, H * 0.955, H * 0.014, 31);
    },
  }),

  /* ---- 조합 CA ---- */
  sheetPiece({
    id: 'refund_forecast',
    name: '환불예상표',
    form: 'report',
    seed: 137,
    note:
      'CA03. 조합 가용현금과 환불부담을 나란히 놓은 표. **환불 막대가 현금 막대보다 길어야 한다** — ' +
      '사업이 멈추면 즉시 무너진다는 게 한눈에 보이는 것이 목적이라, 두 막대 길이가 비슷하면 ' +
      '이 조각은 실패다. 차이 구간을 붉게 칠하고 부족액을 숫자로 찍는다.',
    draw: (g, W, H) => {
      head(g, W, H, 139);
      const bx = W * 0.12;
      const bw = W * 0.76;
      // 가용현금
      g.fillStyle = 'rgba(58,74,96,0.6)';
      g.fillRect(bx, H * 0.28, bw * 0.44, H * 0.08);
      num(g, '212,000,000', bx + bw * 0.46, H * 0.34, H * 0.03, '#2b3444');
      g.strokeStyle = 'rgba(40,52,70,0.55)';
      stroke(g, bx, H * 0.25, bx + bw * 0.3, H * 0.25, H * 0.022, 3);
      // 환불부담
      g.fillStyle = 'rgba(168,60,46,0.66)';
      g.fillRect(bx, H * 0.46, bw * 0.92, H * 0.08);
      num(g, '441,000,000', bx + bw * 0.02, H * 0.52, H * 0.03, '#f3ece0');
      g.strokeStyle = 'rgba(40,52,70,0.55)';
      stroke(g, bx, H * 0.43, bx + bw * 0.32, H * 0.43, H * 0.022, 7);
      // 부족분
      g.strokeStyle = '#b6382b';
      g.lineWidth = 2.4;
      g.setLineDash([8, 6]);
      g.beginPath();
      g.moveTo(bx + bw * 0.44, H * 0.26);
      g.lineTo(bx + bw * 0.44, H * 0.6);
      g.stroke();
      g.setLineDash([]);
      g.fillStyle = 'rgba(178,52,40,0.16)';
      g.fillRect(bx + bw * 0.44, H * 0.46, bw * 0.48, H * 0.08);
      num(g, '- 229,000,000', bx + bw * 0.92, H * 0.64, H * 0.042, '#a8392c', 'right');
      table(g, bx, H * 0.7, bw, H * 0.2, 4, 3, 149);
    },
  }),

  /* ---- 작업실 WS ---- */
  sheetPiece({
    id: 'handwriting_match',
    name: '필체 대조표',
    form: 'report',
    seed: 151,
    note:
      'WS03. 명주원의 필체 샘플과 탄 메모의 필체를 나란히 붙인 대조표. **두 필체가 같아 보여야 ' +
      '한다** — 위아래 글씨의 기울기와 흔들림을 같은 seed 로 그려 실제로 닮게 만들고, ' +
      '사이를 잇는 붉은 대조선 다섯 개가 「대조했다」를 말해 준다. 아래쪽 샘플은 가장자리가 ' +
      '탄 종이 위에 붙어 있어야 탄 메모에서 온 것이 읽힌다.',
    bg: '#e8eaea',
    draw: (g, W, H) => {
      head(g, W, H, 157);
      // 위: 필체 샘플
      g.fillStyle = '#efe9dc';
      g.fillRect(W * 0.1, H * 0.2, W * 0.8, H * 0.24);
      g.strokeStyle = 'rgba(40,52,70,0.35)';
      g.lineWidth = 1.4;
      g.strokeRect(W * 0.1, H * 0.2, W * 0.8, H * 0.24);
      for (let i = 0; i < 4; i++) hand(g, W * 0.14, H * (0.26 + i * 0.05), W * (0.5 + rnd(i * 3) * 0.2), H * 0.026, i * 5);
      num(g, 'A', W * 0.86, H * 0.245, H * 0.036, 'rgba(40,52,70,0.6)', 'right');
      // 아래: 탄 메모 조각 — 가장자리가 그을렸다
      g.fillStyle = '#d9cdb4';
      g.fillRect(W * 0.1, H * 0.56, W * 0.8, H * 0.24);
      // **그을음은 blur 로 번지게 그린다.** 원을 40개 찍으면 그을음이 아니라
      // 구슬 목걸이가 된다(drag_mark 에서 이미 한 번 당했다). 위아래 가장자리를
      // 들쭉날쭉한 폐곡선 하나로 그린 뒤 통째로 흐린다.
      g.save();
      g.filter = 'blur(9px)';
      g.fillStyle = 'rgba(44,34,24,0.8)';
      for (const [y0, dir, sd] of [
        [0.56, 1, 0],
        [0.8, -1, 31],
      ]) {
        g.beginPath();
        g.moveTo(W * 0.1, H * y0);
        for (let i = 0; i <= 26; i++) {
          const t = i / 26;
          g.lineTo(W * (0.1 + t * 0.8), H * (y0 + dir * (0.012 + rnd(sd + i * 1.7) * 0.026)));
        }
        g.lineTo(W * 0.9, H * (y0 - dir * 0.006));
        g.lineTo(W * 0.1, H * (y0 - dir * 0.006));
        g.closePath();
        g.fill();
      }
      g.restore();
      g.filter = 'none';
      for (let i = 0; i < 4; i++) hand(g, W * 0.14, H * (0.62 + i * 0.05), W * (0.5 + rnd(i * 3) * 0.2), H * 0.026, i * 5);
      num(g, 'B', W * 0.86, H * 0.605, H * 0.036, 'rgba(40,52,70,0.6)', 'right');
      // 대조선 — **그을음 띠를 피해 두 샘플 사이 빈 칸에만 긋는다.**
      // 원래 H*0.45~0.55 로 그어 아래쪽 끝이 그을음에 먹혔고, 2px 이라 아예 안 보였다.
      g.strokeStyle = '#b6382b';
      g.fillStyle = '#b6382b';
      g.lineWidth = 4;
      g.lineCap = 'round';
      for (let i = 0; i < 5; i++) {
        const x = W * (0.18 + i * 0.16);
        g.beginPath();
        g.moveTo(x, H * 0.455);
        g.lineTo(x + W * 0.028, H * 0.542);
        g.stroke();
        g.beginPath();
        g.arc(x, H * 0.455, 5, 0, Math.PI * 2);
        g.fill();
      }
      g.lineCap = 'butt';
      num(g, 'A = B', W * 0.5, H * 0.9, H * 0.05, '#a8392c', 'center');
    },
  }),

  /* ---- 개인 PR ---- */
  sheetPiece({
    id: 'closure_papers',
    name: '공방 폐업 서류',
    form: 'folder',
    seed: 163,
    note:
      'PR01. 예전 공방의 폐업·채무조정 서류. **명주원이 이미 한 번 잃었다는 증거다** — 그래서 ' +
      '새 서류가 아니라 오래 보관된 서류로 보여야 한다: 종이가 누렇고 접힌 자국이 가로로 나 있고, ' +
      '붉은 「폐업」 사선 도장이 표지를 가로지른다.',
    bg: '#d8c8a4',
    draw: (g, W, H) => {
      head(g, W, H, 167, '#4a3c22');
      table(g, W * 0.1, H * 0.2, W * 0.8, H * 0.34, 5, 3, 173);
      body(g, W * 0.1, H * 0.6, W * 0.76, 4, H * 0.042, 179, 'rgba(64,54,34,0.6)');
      seal(g, W * 0.56, H * 0.78, W * 0.13, 11);
      // 접힌 자국 두 줄
      g.strokeStyle = 'rgba(90,76,48,0.28)';
      g.lineWidth = 3;
      for (const y of [H / 3, (H * 2) / 3]) {
        g.beginPath();
        g.moveTo(0, y);
        g.lineTo(W, y);
        g.stroke();
      }
      // 사선 도장
      g.save();
      g.translate(W * 0.5, H * 0.46);
      g.rotate(-0.34);
      g.strokeStyle = 'rgba(170,48,38,0.62)';
      g.lineWidth = 6;
      g.strokeRect(-W * 0.3, -H * 0.06, W * 0.6, H * 0.12);
      g.strokeStyle = 'rgba(170,48,38,0.72)';
      stroke(g, -W * 0.24, H * 0.015, W * 0.24, H * 0.015, H * 0.055, 5);
      g.restore();
    },
  }),

  sheetPiece({
    id: 'workshop_mou',
    name: '장기 작업장 운영협의 초안',
    form: 'report',
    seed: 181,
    note:
      'PR02. 명주원이 잃게 될 미래. 아직 초안이라 **서명란이 비어 있고 여백에 손글씨 메모가 ' +
      '달려 있다** — 확정 계약처럼 보이면 「잃을 뻔한 것」이 아니라 「가진 것」이 되어 동기가 ' +
      '약해진다. DRAFT 사선 워터마크와 빈 서명선 둘이 필수다.',
    draw: (g, W, H) => {
      head(g, W, H, 191);
      body(g, W * 0.1, H * 0.2, W * 0.66, 3, H * 0.042, 193);
      for (let i = 0; i < 4; i++) {
        num(g, `${i + 1}`, W * 0.1, H * (0.4 + i * 0.078), H * 0.028, 'rgba(40,52,70,0.6)');
        body(g, W * 0.16, H * (0.395 + i * 0.078), W * 0.6, 1, H * 0.032, 197 + i * 3, 'rgba(38,48,64,0.82)');
      }
      // 여백 손글씨
      hand(g, W * 0.72, H * 0.44, W * 0.2, H * 0.024, 3);
      hand(g, W * 0.72, H * 0.48, W * 0.16, H * 0.024, 7);
      // 빈 서명선 둘
      g.strokeStyle = 'rgba(40,52,70,0.72)';
      g.lineWidth = 3.4;
      for (const x of [0.12, 0.54]) {
        g.beginPath();
        g.moveTo(W * x, H * 0.84);
        g.lineTo(W * (x + 0.34), H * 0.84);
        g.stroke();
      }
      // DRAFT 워터마크
      g.save();
      g.translate(W * 0.5, H * 0.56);
      g.rotate(-0.4);
      num(g, 'DRAFT', 0, 0, H * 0.12, 'rgba(70,86,110,0.32)', 'center');
      g.restore();
    },
  }),

  sheetPiece({
    id: 'exhibit_contract',
    mount: 'surface',
    name: '사진집·외부전시 계약 초안',
    form: 'report',
    seed: 199,
    note:
      'PR03. 송이경의 사진집·전시 계약 초안. **조작이 드러나면 이 계약이 날아간다** — 조항이 ' +
      '빽빽해야 「커리어가 걸린 문서」로 보이고, 금액 칸과 빈 서명란이 함께 있어야 아직 ' +
      '확정 전이라는 것이 읽힌다.',
    draw: (g, W, H) => {
      head(g, W, H, 211);
      for (let i = 0; i < 7; i++) {
        num(g, `${i + 1}`, W * 0.1, H * (0.22 + i * 0.072), H * 0.026, 'rgba(40,52,70,0.55)');
        body(g, W * 0.15, H * (0.215 + i * 0.072), W * 0.72, 1, H * 0.03, 223 + i * 5, 'rgba(38,48,64,0.82)');
      }
      g.strokeStyle = 'rgba(40,52,70,0.7)';
      g.lineWidth = 3;
      g.strokeRect(W * 0.5, H * 0.74, W * 0.38, H * 0.08);
      num(g, '48,000,000', W * 0.86, H * 0.795, H * 0.032, '#2b3444', 'right');
      g.beginPath();
      g.moveTo(W * 0.12, H * 0.9);
      g.lineTo(W * 0.42, H * 0.9);
      g.stroke();
      g.beginPath();
      g.moveTo(W * 0.54, H * 0.9);
      g.lineTo(W * 0.86, H * 0.9);
      g.stroke();
    },
  }),

  sheetPiece({
    id: 'buyer_trace',
    name: '구매자 추적 목록',
    form: 'clipboard',
    seed: 227,
    note:
      'PR05. 윤가람이 손으로 정리한 구매자·판매처 목록. **인쇄물이 아니라 손글씨여야 한다** — ' +
      '공식 기록이 아니라 혼자 몰래 추적했다는 것이 요점이다. 모눈종이 위에 손글씨, 회수된 줄에는 ' +
      '체크, 못 찾은 줄에는 물음표가 섞여 있어야 「진행 중」으로 읽힌다.',
    bg: '#e6e8e0',
    draw: (g, W, H) => {
      // 모눈
      g.strokeStyle = 'rgba(78,108,120,0.32)';
      g.lineWidth = 1;
      for (let x = 0; x < W; x += W / 18) {
        g.beginPath();
        g.moveTo(x, 0);
        g.lineTo(x, H);
        g.stroke();
      }
      for (let y = 0; y < H; y += W / 18) {
        g.beginPath();
        g.moveTo(0, y);
        g.lineTo(W, y);
        g.stroke();
      }
      hand(g, W * 0.1, H * 0.1, W * 0.42, H * 0.034, 1);
      g.strokeStyle = 'rgba(38,52,92,0.5)';
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(W * 0.08, H * 0.13);
      g.lineTo(W * 0.92, H * 0.13);
      g.stroke();
      for (let i = 0; i < 9; i++) {
        const y = H * (0.2 + i * 0.078);
        hand(g, W * 0.12, y, W * (0.3 + rnd(i * 3) * 0.14), H * 0.026, i * 5);
        hand(g, W * 0.56, y, W * (0.16 + rnd(i * 7) * 0.1), H * 0.026, i * 9 + 2);
        if (rnd(i * 11) > 0.44) check(g, W * 0.88, y - H * 0.008, H * 0.03, '#2f4b34');
        else num(g, '?', W * 0.88, y, H * 0.036, '#a8392c', 'center');
      }
      num(g, '4 / 9', W * 0.9, H * 0.95, H * 0.036, 'rgba(38,52,92,0.8)', 'right');
    },
  }),

  sheetPiece({
    id: 'sponsor_clause',
    name: '협찬계약 해지·환수 조항',
    form: 'loose',
    seed: 233,
    note:
      'PR07. 차유리의 협찬·광고 계약 중 해지·환수 조항. **폭로되면 돈을 토해내야 한다** — ' +
      '조항이 빽빽한 가운데 한 조항만 붉은 상자로 둘러싸여 있어야 「이 한 줄이 문제」가 읽힌다. ' +
      '상자 안에 환수 금액이 숫자로 있어야 위협이 구체화된다.',
    draw: (g, W, H) => {
      head(g, W, H, 239);
      for (let i = 0; i < 5; i++) {
        num(g, `${i + 1}`, W * 0.1, H * (0.21 + i * 0.058), H * 0.022, 'rgba(40,52,70,0.5)');
        body(g, W * 0.15, H * (0.205 + i * 0.058), W * 0.72, 1, H * 0.026, 241 + i * 3, 'rgba(38,48,64,0.8)');
      }
      // 문제의 조항
      g.fillStyle = 'rgba(178,52,40,0.08)';
      g.fillRect(W * 0.08, H * 0.52, W * 0.84, H * 0.15);
      g.strokeStyle = '#b6382b';
      g.lineWidth = 2.6;
      g.strokeRect(W * 0.08, H * 0.52, W * 0.84, H * 0.15);
      num(g, '6', W * 0.11, H * 0.565, H * 0.028, '#a8392c');
      g.strokeStyle = 'rgba(120,36,28,0.85)';
      stroke(g, W * 0.16, H * 0.56, W * 0.84, H * 0.56, H * 0.013, 13);
      stroke(g, W * 0.16, H * 0.598, W * 0.56, H * 0.598, H * 0.013, 17);
      num(g, '100% x 1.5', W * 0.16, H * 0.652, H * 0.034, '#a8392c');
      for (let i = 0; i < 5; i++) {
        num(g, `${i + 7}`, W * 0.1, H * (0.72 + i * 0.052), H * 0.022, 'rgba(40,52,70,0.5)');
        body(g, W * 0.15, H * (0.715 + i * 0.052), W * 0.7, 1, H * 0.026, 251 + i * 3, 'rgba(38,48,64,0.8)');
      }
    },
  }),

  sheetPiece({
    id: 'apology_script',
    name: '사과영상 대본',
    form: 'loose',
    seed: 257,
    note:
      'PR08. 차유리가 써 두기만 하고 찍지는 않은 사과영상 대본. **찍지 않았다는 것이 단서다** — ' +
      '대본은 완성돼 있는데 상단 촬영정보칸의 시간이 `00:00` 이고 그 위에 붉은 사선이 그어져 ' +
      '있어야 「준비만 하고 멈췄다」가 읽힌다. 대본이라 글줄이 가운데로 몰린다.',
    draw: (g, W, H) => {
      head(g, W, H, 263);
      // 촬영정보 — 찍히지 않았다
      g.strokeStyle = 'rgba(40,52,70,0.5)';
      g.lineWidth = 1.6;
      g.strokeRect(W * 0.1, H * 0.18, W * 0.8, H * 0.09);
      num(g, 'REC', W * 0.14, H * 0.238, H * 0.034, 'rgba(40,52,70,0.55)');
      num(g, '00:00', W * 0.86, H * 0.238, H * 0.04, '#a8392c', 'right');
      g.strokeStyle = 'rgba(178,52,40,0.8)';
      g.lineWidth = 3.4;
      g.beginPath();
      g.moveTo(W * 0.12, H * 0.265);
      g.lineTo(W * 0.88, H * 0.185);
      g.stroke();
      // 대본 — 가운데로 몰린 짧은 줄들
      g.strokeStyle = 'rgba(38,48,64,0.66)';
      for (let i = 0; i < 11; i++) {
        const len = W * (0.28 + rnd(i * 7) * 0.3);
        const y = H * (0.35 + i * 0.05);
        stroke(g, W * 0.5 - len / 2, y, W * 0.5 + len / 2, y, H * 0.02, i * 5);
      }
      hand(g, W * 0.62, H * 0.93, W * 0.24, H * 0.026, 5);
    },
  }),

  sheetPiece({
    id: 'mortgage_notice',
    name: '담보설정 통지서 사본',
    form: 'report',
    seed: 271,
    note:
      'PR09. 배정호 어머니가 사는 집에 근저당이 잡혔다는 은행 통지서의 **사본**. ' +
      '**사본이라는 것과 금액이 전부다** — 채권최고액 한 줄이 다른 글자의 두 배로 크고 붉으며, ' +
      '그 아래 목적물 주소가 한 줄. 맨 아래 「설정 완료」 도장은 원본이 아니라 복사된 것이라 ' +
      '흐리게 찍히고, 종이 가운데에 **한 번 접혔던 가로 자국**이 남아 있어야 「받아서 접어 들고 ' +
      '왔다」가 읽힌다. 접힘선이 없으면 그냥 은행 서식이 된다.',
    draw: (g, W, H) => {
      head(g, W, H, 277);
      // 채권최고액 — 이 서류의 전부
      num(g, '채권최고액', W * 0.1, H * 0.29, H * 0.03, 'rgba(40,52,70,0.6)');
      num(g, '180,000,000', W * 0.9, H * 0.3, H * 0.055, '#a8392c', 'right');
      // 목적물 — 어머니가 사는 집 주소 한 줄
      num(g, '목적물', W * 0.1, H * 0.39, H * 0.026, 'rgba(40,52,70,0.6)');
      body(g, W * 0.26, H * 0.375, W * 0.64, 1, H * 0.03, 179);
      table(g, W * 0.1, H * 0.45, W * 0.8, H * 0.26, 4, 2, 181);
      // 복사된 도장 — 흐리다
      g.save();
      g.globalAlpha = 0.42;
      seal(g, W * 0.58, H * 0.75, W * 0.1, 183);
      g.restore();
      // 한 번 접혔던 자국
      g.strokeStyle = 'rgba(40,52,70,0.34)';   // 0.22 는 컷에서 안 읽혔다
      g.lineWidth = 2.6;
      g.beginPath();
      g.moveTo(0, H * 0.5);
      g.lineTo(W, H * 0.502);
      g.stroke();
      g.strokeStyle = 'rgba(255,255,255,0.5)';   // 접힌 자리 바로 아래가 밝다 — 종이가 꺾인 티
      g.lineWidth = 2.0;
      g.beginPath();
      g.moveTo(0, H * 0.508);
      g.lineTo(W, H * 0.51);
      g.stroke();
    },
  }),

  /* ---- 조합센터 CC — 초안에는 있는데 조각이 없던 둘 ---- */
  sheetPiece({
    id: 'key_rules',
    name: '비상키 운영규칙',
    form: 'report',
    seed: 301,
    note:
      'CC03. 공용 비상키를 언제 쓸 수 있는지 적은 규칙문. **요점은 「관리자 독점권 없음」이다** — ' +
      '사용 가능 사유 셋(화재·동파·누수)이 나열된 아래에, 관리자 전용 조항이 붉은 줄로 ' +
      '통째로 지워져 있어야 한다. 지워진 자국이 없으면 이 문서는 명주원 독점키설을 못 깬다.',
    draw: (g, W, H) => {
      head(g, W, H, 301);
      num(g, '3', W * 0.9, H * 0.115, H * 0.03, 'rgba(40,52,70,0.6)', 'right');
      // 사용 가능 사유 세 줄
      for (let i = 0; i < 3; i++) {
        const y = H * (0.235 + i * 0.088);
        num(g, `0${i + 1}`, W * 0.12, y + H * 0.012, H * 0.03, 'rgba(40,52,70,0.62)');
        g.strokeStyle = 'rgba(38,48,64,0.72)';
        stroke(g, W * 0.21, y + H * 0.012, W * (0.52 + rnd(301 + i * 3) * 0.26), y + H * 0.012, H * 0.024, 301 + i);
      }
      // **독점권 조항이 통째로 지워져 있다** — 이 서류의 전부다
      g.fillStyle = 'rgba(178,52,40,0.07)';
      g.fillRect(W * 0.1, H * 0.55, W * 0.8, H * 0.17);
      g.strokeStyle = 'rgba(178,52,40,0.7)';
      g.lineWidth = 2.4;
      g.strokeRect(W * 0.1, H * 0.55, W * 0.8, H * 0.17);
      g.strokeStyle = 'rgba(38,48,64,0.5)';
      stroke(g, W * 0.14, H * 0.6, W * 0.82, H * 0.6, H * 0.022, 311);
      stroke(g, W * 0.14, H * 0.66, W * 0.62, H * 0.66, H * 0.022, 313);
      g.strokeStyle = '#b6382b';
      g.lineWidth = 4.5;
      g.lineCap = 'round';
      g.beginPath();
      g.moveTo(W * 0.12, H * 0.612);
      g.lineTo(W * 0.86, H * 0.596);
      g.moveTo(W * 0.12, H * 0.668);
      g.lineTo(W * 0.66, H * 0.656);
      g.stroke();
      g.lineCap = 'butt';
      cross(g, W * 0.9, H * 0.635, H * 0.045);
      // 하단 시행일
      g.strokeStyle = 'rgba(40,52,70,0.4)';
      g.lineWidth = 1.6;
      g.beginPath();
      g.moveTo(W * 0.1, H * 0.82);
      g.lineTo(W * 0.9, H * 0.82);
      g.stroke();
      num(g, '12.20', W * 0.9, H * 0.885, H * 0.034, 'rgba(40,52,70,0.62)', 'right');
    },
  }),

  sheetPiece({
    id: 'transfer_summary',
    name: '전환금 계약 요약본',
    form: 'folder',
    seed: 317,
    note:
      'CC09·CC10. 전환금 계약의 요약 한 장. **큰 금액과 하단 조항 둘 다 읽혀야 한다** — ' +
      '위쪽에 `640,000,000` 과 `12.19 18:00` 이, 아래쪽 붉은 칸에 「대표자 사고 시 지급보류」 ' +
      '조항이 있어야 한다. 금액만 있으면 사업돈이고, 조항이 같이 있어야 **사망 은폐 동기**가 된다.',
    draw: (g, W, H) => {
      head(g, W, H, 317);
      // 금액 — 이 서류에서 가장 큰 글씨
      g.fillStyle = 'rgba(52,66,86,0.1)';
      g.fillRect(W * 0.1, H * 0.19, W * 0.8, H * 0.14);
      g.strokeStyle = 'rgba(40,52,70,0.55)';
      g.lineWidth = 1.8;
      g.strokeRect(W * 0.1, H * 0.19, W * 0.8, H * 0.14);
      num(g, '640,000,000', W * 0.87, H * 0.293, H * 0.062, '#20293a', 'right');
      num(g, 'KRW', W * 0.14, H * 0.293, H * 0.03, 'rgba(40,52,70,0.55)');
      // 지급 예정
      g.strokeStyle = 'rgba(38,48,64,0.7)';
      stroke(g, W * 0.12, H * 0.39, W * 0.42, H * 0.39, H * 0.024, 321);
      num(g, '12.19  18:00', W * 0.88, H * 0.4, H * 0.042, '#2b3444', 'right');
      // 중간 본문 — 줄 높이가 곧 획 두께다(`body` 는 max(1.4, lh*0.2) 를 쓴다).
      // H*0.024 로 뽑았더니 3.4px 실선이 되어 렌더에서 종이 결에 묻혔다.
      for (let i = 0; i < 4; i++) {
        body(g, W * 0.12, H * (0.455 + i * 0.052), W * 0.74, 1, H * 0.033, 323 + i * 3, 'rgba(38,48,64,0.86)');
      }
      // **하단 지급보류 조항** — CC10
      g.fillStyle = 'rgba(178,52,40,0.09)';
      g.fillRect(W * 0.1, H * 0.68, W * 0.8, H * 0.16);
      g.strokeStyle = 'rgba(178,52,40,0.78)';
      g.lineWidth = 2.6;
      g.strokeRect(W * 0.1, H * 0.68, W * 0.8, H * 0.16);
      g.strokeStyle = 'rgba(120,36,28,0.85)';
      stroke(g, W * 0.22, H * 0.716, W * 0.86, H * 0.716, H * 0.015, 331);
      stroke(g, W * 0.22, H * 0.788, W * 0.72, H * 0.788, H * 0.015, 333);
      // 경고 표시는 **삼각형 + 느낌표** 로 그린다. 글자만 찍었더니 붉은 칸 안에서
      // 획이 가늘어 아예 안 보였다. 동그라미도 칸 전체를 두르면 붉은 테두리와
      // 겹쳐 읽히지 않아, 지급보류 조항 한 줄만 감싸도록 줄였다.
      g.fillStyle = '#b6382b';
      g.beginPath();
      g.moveTo(W * 0.148, H * 0.708);
      g.lineTo(W * 0.19, H * 0.782);
      g.lineTo(W * 0.106, H * 0.782);
      g.closePath();
      g.fill();
      num(g, '!', W * 0.148, H * 0.772, H * 0.05, '#f2ece0', 'center');
      ring(g, W * 0.47, H * 0.788, W * 0.29, H * 0.028, 337);
    },
  }),

  /* ---- 기믹 힌트 원본 — 코드가 적힌 종이가 없으면 기믹이 성립하지 않는다 ---- */
  {
    ...sheetPiece({
    id: 'firing_chart',
    mount: 'surface',
    name: '소성표',
    form: 'clipboard',
    seed: 341,
    note:
      'PT01 의 힌트 원본. 유약장 4자리 자물쇠 코드 `1250` 이 여기서 나온다. ' +
      '**최고온도 숫자가 판에서 제일 큰 글씨여야 한다** — 온도 곡선의 평탄부 위에 `1250` 을 ' +
      '큼직하게 찍고 동그라미를 두른다. 곡선만 있고 숫자가 작으면 플레이어는 코드를 못 찾는다.',
    draw: (g, W, H) => {
      head(g, W, H, 341);
      const x = W * 0.14;
      const y = H * 0.26;
      const wid = W * 0.74;
      const hgt = H * 0.42;
      // 축
      g.strokeStyle = 'rgba(40,52,70,0.45)';
      g.lineWidth = 1.6;
      g.beginPath();
      g.moveTo(x, y);
      g.lineTo(x, y + hgt);
      g.lineTo(x + wid, y + hgt);
      g.stroke();
      // 눈금
      g.strokeStyle = 'rgba(40,52,70,0.16)';
      for (let i = 1; i < 5; i++) {
        g.beginPath();
        g.moveTo(x, y + (hgt * i) / 5);
        g.lineTo(x + wid, y + (hgt * i) / 5);
        g.stroke();
      }
      // 소성 곡선 — 올라가서 평탄, 그리고 내려온다
      g.strokeStyle = '#2b3444';
      g.lineWidth = 3.4;
      g.lineJoin = 'round';
      g.beginPath();
      const pk = 0.12;
      for (let i = 0; i <= 40; i++) {
        const t = i / 40;
        let v: number;
        if (t < 0.55) v = Math.pow(t / 0.55, 1.35) * 0.9;
        else if (t < 0.72) v = 0.9;
        else v = 0.9 - Math.pow((t - 0.72) / 0.28, 1.6) * 0.72;
        const px = x + wid * t;
        const py = y + hgt - hgt * (pk + v * 0.82);
        if (i === 0) g.moveTo(px, py);
        else g.lineTo(px, py);
      }
      g.stroke();
      const peakY = y + hgt - hgt * (pk + 0.9 * 0.82);
      // 평탄부 표시
      g.strokeStyle = '#b6382b';
      g.lineWidth = 2.2;
      g.setLineDash([8, 6]);
      g.beginPath();
      g.moveTo(x, peakY);
      g.lineTo(x + wid, peakY);
      g.stroke();
      g.setLineDash([]);
      // **코드 숫자.** 평탄부 바로 위(t=0.63)에 얹었더니 동그라미가 평탄부를 통째로
      // 덮어, 정작 「최고온도에서 평평해진다」는 그림이 사라졌다. 곡선이 내려간 뒤의
      // 빈 오른쪽으로 빼고 평탄부 점선이 거기까지 이어져 지시선 노릇을 하게 둔다
      num(g, '1250', x + wid * 0.86, peakY - H * 0.028, H * 0.072, '#a8392c', 'center');
      ring(g, x + wid * 0.86, peakY - H * 0.052, W * 0.145, H * 0.048, 343);
      // 시간축 숫자
      for (let i = 0; i < 5; i++) {
        num(g, `${i * 3}`, x + (wid * i) / 4, y + hgt + H * 0.038, H * 0.028, 'rgba(40,52,70,0.6)', 'center');
      }
      // 하단 표
      table(g, W * 0.14, H * 0.75, W * 0.74, H * 0.14, 3, 4, 347);
    },
  }),
    /* 머리맡 집게를 젖혀 종이를 푼다 (2026-09-17) */
    openable: [{ part: '집게', swing: 1.5, axis: 'x', pivot: [0, 0.011, 0.161] }],
  },

  sheetPiece({
    id: 'roast_profile',
    name: '로스팅 프로파일',
    form: 'report',
    seed: 353,
    note:
      'CA06 의 힌트 원본. 로스터리 태블릿 PIN `0947` 이 여기 `1차 크랙 09:47` 에서 나온다. ' +
      '**시각이 커야 하고, 곡선 위 그 지점에 표시가 찍혀 있어야 한다** — 곡선 어디가 1차 크랙인지 ' +
      '안 보이면 플레이어는 표의 숫자 여럿 중 무엇이 코드인지 고를 수 없다.',
    draw: (g, W, H) => {
      head(g, W, H, 353);
      const x = W * 0.13;
      // **그래프를 내려 잡았다.** 처음엔 y=H*0.24 에 두고 `09:47` 라벨을 도면 안에
      // 넣었는데, 라벨과 동그라미가 곡선 오른쪽 절반을 가려 「어디가 1차 크랙인지」가
      // 오히려 안 보였다. 라벨은 도면 **위**, 점선이 라벨에서 곡선까지 내려가는
      // 지시선 노릇을 한다 — snow_record 의 23:21 표시와 같은 처리다.
      const y = H * 0.32;
      const wid = W * 0.76;
      const hgt = H * 0.34;
      g.strokeStyle = 'rgba(40,52,70,0.45)';
      g.lineWidth = 1.6;
      g.beginPath();
      g.moveTo(x, y);
      g.lineTo(x, y + hgt);
      g.lineTo(x + wid, y + hgt);
      g.stroke();
      g.strokeStyle = 'rgba(40,52,70,0.14)';
      for (let i = 1; i < 4; i++) {
        g.beginPath();
        g.moveTo(x, y + (hgt * i) / 4);
        g.lineTo(x + wid, y + (hgt * i) / 4);
        g.stroke();
      }
      // 원두 온도 곡선 — 처음 급락 후 완만한 상승(로스팅 곡선의 특징)
      g.strokeStyle = '#5a3a24';
      g.lineWidth = 3.4;
      g.lineJoin = 'round';
      g.beginPath();
      const cy = (t: number) => {
        const v = t < 0.12 ? 0.7 - (t / 0.12) * 0.42 : 0.28 + Math.pow((t - 0.12) / 0.88, 0.72) * 0.62;
        return y + hgt - hgt * v;
      };
      for (let i = 0; i <= 44; i++) {
        const t = i / 44;
        if (i === 0) g.moveTo(x, cy(t));
        else g.lineTo(x + wid * t, cy(t));
      }
      g.stroke();
      // 1차 크랙 지점 — 세로선 + 점 + 시각
      const ct = 0.62;
      g.strokeStyle = '#b6382b';
      g.lineWidth = 2.4;
      g.setLineDash([7, 6]);
      g.beginPath();
      g.moveTo(x + wid * ct, y - H * 0.055);
      g.lineTo(x + wid * ct, y + hgt);
      g.stroke();
      g.setLineDash([]);
      g.fillStyle = '#b6382b';
      g.beginPath();
      g.arc(x + wid * ct, cy(ct), 7, 0, Math.PI * 2);
      g.fill();
      num(g, '09:47', x + wid * ct, y - H * 0.075, H * 0.062, '#a8392c', 'center');
      ring(g, x + wid * ct, y - H * 0.093, W * 0.15, H * 0.042, 359);
      // 하단 기록표
      table(g, W * 0.13, H * 0.7, W * 0.76, H * 0.2, 4, 3, 361);
      num(g, '09:47', W * 0.78, H * 0.795, H * 0.03, '#a8392c', 'right');
    },
  }),

  /* ---- 작업실 WS ---- */
  sheetPiece({
    id: 'roof_repair',
    name: '지붕 균열 사진·임시보강 기록',
    form: 'folder',
    seed: 367,
    note:
      'WS04. 숙박동 지붕 균열 사진 두 장과 그 아래 보강 기록. **「임시」라는 것이 형태로 보여야 한다** — ' +
      '사진에 균열선이 있고, 기록칸 중 「영구」 칸은 비어 있고 「임시」 칸에만 표시가 있어야 한다. ' +
      '둘 다 채워지면 실제 안전문제가 해소된 것처럼 읽혀 단서가 죽는다.',
    draw: (g, W, H) => {
      head(g, W, H, 367);
      // 사진 두 장 — 회색 판에 균열 폴리라인
      for (let k = 0; k < 2; k++) {
        const px = W * (0.11 + k * 0.42);
        const py = H * 0.2;
        const pw = W * 0.36;
        const ph = H * 0.26;
        g.fillStyle = k ? '#6f7379' : '#7b7f85';
        g.fillRect(px, py, pw, ph);
        g.strokeStyle = 'rgba(20,26,34,0.6)';
        g.lineWidth = 2;
        g.strokeRect(px, py, pw, ph);
        // 지붕 결 — 가로 널
        g.strokeStyle = 'rgba(30,36,44,0.28)';
        g.lineWidth = 2;
        for (let i = 1; i < 5; i++) {
          g.beginPath();
          g.moveTo(px, py + (ph * i) / 5);
          g.lineTo(px + pw, py + (ph * i) / 5);
          g.stroke();
        }
        // 균열
        g.strokeStyle = 'rgba(16,18,22,0.9)';
        g.lineWidth = 3.2;
        g.lineJoin = 'round';
        g.beginPath();
        g.moveTo(px + pw * 0.12, py + ph * 0.82);
        for (let i = 1; i <= 7; i++) {
          const t = i / 7;
          g.lineTo(px + pw * (0.12 + t * 0.76), py + ph * (0.82 - t * 0.6 + (rnd(367 + k * 9 + i) - 0.5) * 0.22));
        }
        g.stroke();
        ring(g, px + pw * 0.55, py + ph * 0.5, pw * 0.3, ph * 0.28, 371 + k * 5);
        num(g, `0${k + 1}`, px + pw - 8, py + ph - 8, H * 0.03, 'rgba(232,236,240,0.85)', 'right');
      }
      // 보강 기록 — 두 칸 중 하나만 표시
      const bx = W * 0.11;
      const by = H * 0.54;
      g.strokeStyle = 'rgba(40,52,70,0.55)';
      g.lineWidth = 1.8;
      g.strokeRect(bx, by, W * 0.78, H * 0.16);
      g.beginPath();
      g.moveTo(bx + W * 0.39, by);
      g.lineTo(bx + W * 0.39, by + H * 0.16);
      g.stroke();
      boxMark(g, bx + W * 0.06, by + H * 0.09, H * 0.034);
      check(g, bx + W * 0.06, by + H * 0.09, H * 0.036);
      g.strokeStyle = 'rgba(38,48,64,0.72)';
      stroke(g, bx + W * 0.11, by + H * 0.1, bx + W * 0.33, by + H * 0.1, H * 0.024, 373);
      boxMark(g, bx + W * 0.45, by + H * 0.09, H * 0.034);
      g.strokeStyle = 'rgba(38,48,64,0.4)';
      stroke(g, bx + W * 0.5, by + H * 0.1, bx + W * 0.72, by + H * 0.1, H * 0.024, 377);
      // 재확인 요망 — 손글씨
      hand(g, W * 0.14, H * 0.79, W * 0.56, H * 0.03, 9);
      hand(g, W * 0.14, H * 0.85, W * 0.38, H * 0.03, 13);
      num(g, '12.16', W * 0.88, H * 0.9, H * 0.032, 'rgba(40,52,70,0.62)', 'right');
    },
  }),

  sheetPiece({
    id: 'early_signoff',
    name: '조기 완료확인서 사본',
    form: 'loose',
    seed: 379,
    note:
      'WS08. 명주원이 서명한 조기 완료확인서의 **사본**이다. 원본이 아니라는 것이 요점이라 ' +
      '`COPY` 사선 워터마크가 판을 가로질러야 하고, 하단 서명란에는 손으로 쓴 서명 하나와 ' +
      '도장이 있어야 한다. 서명이 없으면 「명주원 직업을 무너뜨릴 근거」가 되지 않는다.',
    draw: (g, W, H) => {
      head(g, W, H, 379);
      // 본문
      for (let i = 0; i < 6; i++) {
        body(g, W * 0.12, H * (0.23 + i * 0.055), W * 0.76, 1, H * 0.026, 381 + i * 3, 'rgba(38,48,64,0.76)');
      }
      // 완료일 — 예정보다 이르다
      g.strokeStyle = 'rgba(40,52,70,0.55)';
      g.lineWidth = 1.8;
      g.strokeRect(W * 0.12, H * 0.56, W * 0.76, H * 0.1);
      g.beginPath();
      g.moveTo(W * 0.5, H * 0.56);
      g.lineTo(W * 0.5, H * 0.66);
      g.stroke();
      num(g, '11.28', W * 0.3, H * 0.63, H * 0.042, 'rgba(40,52,70,0.7)', 'center');
      num(g, '10.09', W * 0.7, H * 0.63, H * 0.042, '#a8392c', 'center');
      // 서명란
      g.strokeStyle = 'rgba(40,52,70,0.6)';
      g.lineWidth = 2.6;
      g.beginPath();
      g.moveTo(W * 0.14, H * 0.83);
      g.lineTo(W * 0.56, H * 0.83);
      g.stroke();
      hand(g, W * 0.18, H * 0.822, W * 0.3, H * 0.038, 17);
      seal(g, W * 0.66, H * 0.76, H * 0.06, 383);
      // **사본 워터마크** — 판을 가로지르는 큰 사선 글씨
      g.save();
      g.translate(W * 0.5, H * 0.5);
      g.rotate(-0.55);
      g.globalAlpha = 0.16;
      num(g, 'COPY', 0, 0, H * 0.17, '#46566e', 'center');
      g.restore();
    },
  }),

  /* ---- 외부·환경 EX ---- */
  sheetPiece({
    id: 'lamp_inspect',
    name: '가로등 시설점검 기록',
    form: 'clipboard',
    seed: 389,
    note:
      'EX02. 문제의 가로등이 16일에 정상이었다는 점검표. **모든 칸이 정상 체크여야 한다** — ' +
      '하나라도 이상 표시가 있으면 「그날 밤 얼굴이 보였다」는 목격 신뢰도가 무너진다. ' +
      '날짜 `12.16` 과 등번호가 읽혀야 이 표가 그 가로등의 것이 된다.',
    draw: (g, W, H) => {
      head(g, W, H, 389);
      num(g, '12.16', W * 0.88, H * 0.115, H * 0.032, 'rgba(40,52,70,0.62)', 'right');
      // 점검 항목 여섯 — 전부 체크
      for (let i = 0; i < 6; i++) {
        const y = H * (0.235 + i * 0.088);
        num(g, `L-${12 + i}`, W * 0.13, y + H * 0.012, H * 0.028, 'rgba(40,52,70,0.6)');
        g.strokeStyle = 'rgba(38,48,64,0.7)';
        stroke(g, W * 0.31, y + H * 0.012, W * (0.52 + rnd(389 + i * 3) * 0.16), y + H * 0.012, H * 0.022, 389 + i);
        boxMark(g, W * 0.79, y, H * 0.03);
        check(g, W * 0.79, y, H * 0.032);
        num(g, 'OK', W * 0.86, y + H * 0.012, H * 0.028, '#2f4b34');
      }
      g.strokeStyle = 'rgba(40,52,70,0.4)';
      g.lineWidth = 1.6;
      g.beginPath();
      g.moveTo(W * 0.1, H * 0.79);
      g.lineTo(W * 0.9, H * 0.79);
      g.stroke();
      num(g, '6 / 6', W * 0.9, H * 0.855, H * 0.04, '#2f4b34', 'right');
      hand(g, W * 0.14, H * 0.925, W * 0.26, H * 0.028, 23);
    },
  }),

  sheetPiece({
    id: 'snow_record',
    name: '16일 강설기록',
    form: 'report',
    seed: 397,
    note:
      'EX03. 시간별 강설량과 시정 기록. **23시대 막대가 기준선 아래여야 한다** — 눈은 오지만 ' +
      '시정이 완전히 없어지는 수준은 아니었다는 것이 단서다. 23:21 자리에 세로 표시가 있어야 ' +
      '어느 시각을 봐야 하는지 읽힌다. 막대만 크게 그리면 정반대 결론이 나온다.',
    draw: (g, W, H) => {
      head(g, W, H, 397);
      const x = W * 0.13;
      const y = H * 0.24;
      const wid = W * 0.76;
      const hgt = H * 0.36;
      bars(g, x, y, wid, hgt, [0.12, 0.2, 0.34, 0.46, 0.4, 0.52, 0.44, 0.3], 0.78);
      // 23:21 세로 표시
      const mt = 0.68;
      g.strokeStyle = '#2b3444';
      g.lineWidth = 2.6;
      g.beginPath();
      g.moveTo(x + wid * mt, y - H * 0.02);
      g.lineTo(x + wid * mt, y + hgt);
      g.stroke();
      g.fillStyle = '#2b3444';
      g.beginPath();
      g.moveTo(x + wid * mt, y - H * 0.02);
      g.lineTo(x + wid * mt - 9, y - H * 0.046);
      g.lineTo(x + wid * mt + 9, y - H * 0.046);
      g.closePath();
      g.fill();
      num(g, '23:21', x + wid * mt, y - H * 0.058, H * 0.04, '#2b3444', 'center');
      // 시각 눈금
      for (let i = 0; i < 8; i++) {
        num(g, `${17 + i * 1}`, x + (wid / 8) * (i + 0.5), y + hgt + H * 0.036, H * 0.026, 'rgba(40,52,70,0.55)', 'center');
      }
      // 하단 — 시정 판정. 「완전상실 아님」이 표로 남아야 한다
      const bx = W * 0.13;
      const by = H * 0.7;
      g.strokeStyle = 'rgba(40,52,70,0.55)';
      g.lineWidth = 1.8;
      g.strokeRect(bx, by, W * 0.76, H * 0.18);
      g.beginPath();
      g.moveTo(bx, by + H * 0.09);
      g.lineTo(bx + W * 0.76, by + H * 0.09);
      g.moveTo(bx + W * 0.46, by);
      g.lineTo(bx + W * 0.46, by + H * 0.18);
      g.stroke();
      g.strokeStyle = 'rgba(38,48,64,0.7)';
      stroke(g, bx + W * 0.03, by + H * 0.058, bx + W * 0.4, by + H * 0.058, H * 0.024, 401);
      stroke(g, bx + W * 0.03, by + H * 0.148, bx + W * 0.36, by + H * 0.148, H * 0.024, 403);
      num(g, '400 m', bx + W * 0.72, by + H * 0.062, H * 0.038, '#2b3444', 'right');
      num(g, 'OK', bx + W * 0.72, by + H * 0.152, H * 0.038, '#2f4b34', 'right');
    },
  }),

  sheetPiece({
    id: 'footprint_note',
    name: '발자국 현장설명',
    form: 'loose',
    seed: 409,
    note:
      'EX04. 발자국이 사흘을 못 간다는 것을 그림으로 설명한 한 장. **세 칸이 왼쪽에서 오른쪽으로 ' +
      '옅어져야 한다** — 16일 칸은 또렷하고 19일 칸은 거의 비어야 한다. 세 칸이 비슷하면 ' +
      '「오래된 족적이 없다」가 특정인의 알리바이로 오독된다.',
    draw: (g, W, H) => {
      head(g, W, H, 409);
      const labels = ['12.16', '12.17', '12.19'];
      const alphas = [0.8, 0.34, 0.07];
      for (let k = 0; k < 3; k++) {
        const px = W * (0.09 + k * 0.29);
        const py = H * 0.24;
        const pw = W * 0.25;
        const ph = H * 0.3;
        g.fillStyle = 'rgba(228,232,238,0.85)';
        g.fillRect(px, py, pw, ph);
        g.strokeStyle = 'rgba(40,52,70,0.5)';
        g.lineWidth = 1.8;
        g.strokeRect(px, py, pw, ph);
        // 발자국 — 좌우 번갈아 올라간다
        g.fillStyle = `rgba(48,58,74,${alphas[k]})`;
        for (let i = 0; i < 5; i++) {
          const fy = py + ph * (0.86 - i * 0.17);
          const fx = px + pw * (i % 2 ? 0.6 : 0.38);
          g.save();
          g.translate(fx, fy);
          g.rotate((i % 2 ? 1 : -1) * 0.16);
          g.beginPath();
          g.ellipse(0, 0, pw * 0.06, ph * 0.05, 0, 0, Math.PI * 2);
          g.fill();
          g.beginPath();
          g.ellipse(0, -ph * 0.055, pw * 0.045, ph * 0.028, 0, 0, Math.PI * 2);
          g.fill();
          g.restore();
        }
        num(g, labels[k], px + pw / 2, py + ph + H * 0.048, H * 0.034, 'rgba(40,52,70,0.68)', 'center');
      }
      // 화살표 — 시간이 간다
      g.strokeStyle = 'rgba(40,52,70,0.5)';
      g.lineWidth = 2.6;
      g.beginPath();
      g.moveTo(W * 0.12, H * 0.63);
      g.lineTo(W * 0.86, H * 0.63);
      g.stroke();
      g.fillStyle = 'rgba(40,52,70,0.5)';
      g.beginPath();
      g.moveTo(W * 0.9, H * 0.63);
      g.lineTo(W * 0.86, H * 0.612);
      g.lineTo(W * 0.86, H * 0.648);
      g.closePath();
      g.fill();
      // 설명 본문
      for (let i = 0; i < 4; i++) {
        body(g, W * 0.12, H * (0.71 + i * 0.055), W * 0.76, 1, H * 0.026, 411 + i * 3, 'rgba(38,48,64,0.76)');
      }
    },
  }),
];
